// React
import React from 'react';
import {useState} from 'react';

// MUI
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// others
import * as fbproto from '../messages_pb.js';
import { useFetch } from '../hooks/useFetch.jsx';

// mirrored by the backend and the controller
const MIN_DURATION_S = 1;
const MAX_DURATION_S = 3600;
const DEFAULT_DURATION_S = 30;

// Manual timed run of an actuator (pump/valve/fan/relay) from the device
// page: runs the device for the entered duration, taking precedence over
// the flow engine (e.g. runs despite a daily schedule keeping it off).
export default function RunConfig(props) {
    const [durationS, setDurationS] = useState(DEFAULT_DURATION_S);
    const [running, setRunning] = useState(false);
    const [runStatus, setRunStatus] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarError, setSnackbarError] = useState(false);
    const fetchWrapper = useFetch();
    const ERR_UNKNOWN_ERROR = "Run failed."
    const ERR_DEVICE_NOT_CONNECTED = "Run failed. Please check that the device is connected to the controller."
    const ERR_CONTROLLER_UNREACHABLE = "Run failed. Please check that the controller is connected to wifi."

    function runDevice(e) {
        if (running)
            return;
        const duration = Math.floor(Number(durationS));
        if (!Number.isFinite(duration) || duration < MIN_DURATION_S || duration > MAX_DURATION_S) {
            setSnackbarOpen(true);
            setSnackbarError("Please enter a duration between " + MIN_DURATION_S + " and " + MAX_DURATION_S + " seconds.");
            return;
        }

        const runDeviceBody = {
            deviceId: props.device.deviceId,
            controllerId: props.controllerId,
            durationS: duration
        }
        setRunning(true);
        setRunStatus("");
        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/devices/run`, runDeviceBody)
            .then(response => {
                if (response.ok)
                    return response.json();
                else if (response.status === 504)
                    throw new Error(ERR_CONTROLLER_UNREACHABLE);
                else
                    return response.json();
            })
            .then(responseObj => {
                console.log("run body = " + JSON.stringify(responseObj));
                // sample response: {"device":{"deviceId":5, "controllerId":3}, "error":0}
                if (responseObj.error === 0) {
                    setRunStatus("Running for at least " + duration + " s…");
                }
                else if (responseObj.error === fbproto.Error.ERROR_DEVICE_NOT_CONNECTED) {
                    throw new Error(ERR_DEVICE_NOT_CONNECTED + " (" + responseObj.error + ")");
                }
                else {
                    throw new Error(ERR_UNKNOWN_ERROR + " (" + responseObj.error + ")");
                }
            })
            .catch(error => {
                console.log("error = " + JSON.stringify(error));
                setSnackbarOpen(true);
                setSnackbarError(error.message);
            })
            .finally(() => {
                setRunning(false);
            });
    }

    function handleSnackbarClose(event, reason) {
        setSnackbarOpen(false);
    }

    return (
        <Box sx={{padding:2}}>
            <Typography align='left'>Manual run</Typography>
            <Typography align='left' variant="body2" color="text.secondary">
                Runs this device for the entered duration, taking precedence over flows and schedules.
            </Typography>
            <Box sx={{paddingTop:1, display:'flex', flexDirection:'row', alignItems:'center', gap:1}}>
                <TextField
                    label="Duration (seconds)"
                    type="number"
                    size="small"
                    value={durationS}
                    onChange={(e) => setDurationS(e.target.value)}
                    inputProps={{min: MIN_DURATION_S, max: MAX_DURATION_S, step: 1, style: {width: '8em'}}}
                    disabled={running}
                />
                <LoadingButton size="small" loading={running} variant="contained" onClick={runDevice}>Run</LoadingButton>
            </Box>
            {runStatus !== "" ?
                <Typography align='left' variant="body2" color="text.secondary" sx={{paddingTop:1}}>{runStatus}</Typography> :
                null}
            <Snackbar open={snackbarOpen} onClose={handleSnackbarClose}>
                <Alert severity="error" variant="outlined" sx={{ width: '100%', bgcolor: 'background.paper' }} >
                    {snackbarError}
                </Alert>
            </Snackbar>
        </Box>
    )
};
